/**
 * Ported from yaqatw's src/service/cloud/onedrive.ts. Only the import paths
 * (./events, ./log) and the client-id env var
 * (ONEDRIVE_CLIENT_ID → REACT_APP_ONEDRIVE_CLIENT_ID, CRA's required prefix
 * for inlining) differ from the source.
 */
import Events from "./events";
import { BaseCloudProvider } from "./BaseCloudProvider";
import type { FileMetadata, PushResult, UploadResult } from "./types";
import { log } from "./log";

class OneDriveClass extends BaseCloudProvider {
  public readonly tokenName = "onedriveAccessToken";
  protected readonly providerName = "OneDrive";

  private clientId = process.env.REACT_APP_ONEDRIVE_CLIENT_ID;
  private scopes = "Files.ReadWrite User.Read";
  private appFolderId: string | null = null;
  private appFolderPromise: Promise<string> | null = null;
  private folderCache: Map<string, string> = new Map();
  private folderResolutionPromises: Map<string, Promise<string>> = new Map();

  protected readonly MIN_REQUEST_INTERVAL_MS = 300;

  protected async performAuthenticate(): Promise<string> {
    this.assertClientId(this.clientId, "REACT_APP_ONEDRIVE_CLIENT_ID");
    return this.openAuthDialog(this.getAuthUrl(), { height: 600, width: 600 }).then((r) => {
      if (!r.token) throw new Error("OneDrive authenticate(): no token returned");
      localStorage.setItem(this.tokenName, r.token);
      return r.token;
    });
  }

  protected getAuthUrl(): string {
    const params = new URLSearchParams({
      client_id: this.clientId || "",
      response_type: "token",
      redirect_uri: this.redirectUri,
      scope: this.scopes,
      state: Math.random().toString(36).substring(7),
    });
    return `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?${params}`;
  }

  /**
   * Non-interactive collaboration identity via the User.Read scope this
   * provider already holds: `onedrive:<id>`. Uses the stored token with a plain
   * fetch (no re-auth / popup); returns null if not signed in or the token is
   * stale.
   */
  public async getIdentity(): Promise<string | null> {
    const token = localStorage.getItem(this.tokenName);
    if (!token) return null;
    try {
      const res = await fetch(
        "https://graph.microsoft.com/v1.0/me?$select=id,mail,userPrincipalName",
        {
          headers: { Authorization: `Bearer ${token}` },
          signal: AbortSignal.timeout(this.FETCH_TIMEOUT_MS),
        }
      );
      if (!res.ok) return null;
      const data = await res.json();
      const id = data?.id || data?.mail || data?.userPrincipalName;
      return id ? `onedrive:${id}` : null;
    } catch {
      return null;
    }
  }

  private async ensureAppFolder(): Promise<string> {
    if (this.appFolderId) return this.appFolderId;
    if (this.appFolderPromise) return this.appFolderPromise;

    this.appFolderPromise = (async () => {
      const folderName = "YaqatW-DATA";
      try {
        const checkResponse = await this.fetchWithAuth(
          `https://graph.microsoft.com/v1.0/me/drive/root:/${folderName}`
        );
        if (checkResponse.ok) {
          const data = await checkResponse.json();
          this.appFolderId = data.id;
          return this.appFolderId!;
        }
        const createResponse = await this.fetchWithAuth(
          "https://graph.microsoft.com/v1.0/me/drive/root/children",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name: folderName,
              folder: {},
              "@microsoft.graph.conflictBehavior": "rename",
            }),
          }
        );
        if (!createResponse.ok) {
          throw new Error(`Create failed with status ${createResponse.status}`);
        }
        const createData = await createResponse.json();
        this.appFolderId = createData.id;
        return this.appFolderId!;
      } catch (error) {
        console.error("Error ensuring app folder:", error);
        this.appFolderPromise = null;
        throw error;
      }
    })();

    return this.appFolderPromise;
  }

  private async resolvePath(
    fullPath: string,
    isFolder: boolean = false
  ): Promise<{ parentFolderId: string; fileName: string | null }> {
    let currentParentId = await this.ensureAppFolder();
    const pathParts = fullPath.split("/").filter((p) => p);
    let fileName: string | null = null;

    if (!isFolder && pathParts.length > 0) {
      fileName = pathParts.pop() ?? null;
    }

    for (const part of pathParts) {
      const cacheKey = `${currentParentId}/${part}`;

      if (this.folderCache.has(cacheKey)) {
        currentParentId = this.folderCache.get(cacheKey)!;
        continue;
      }

      if (this.folderResolutionPromises.has(cacheKey)) {
        currentParentId = await this.folderResolutionPromises.get(cacheKey)!;
        continue;
      }

      const parentIdForThisPart = currentParentId;
      const resolutionPromise = (async () => {
        try {
          const checkResponse = await this.fetchWithAuth(
            `https://graph.microsoft.com/v1.0/me/drive/items/${parentIdForThisPart}:/${part}`
          );
          if (checkResponse.ok) {
            const data = await checkResponse.json();
            this.folderCache.set(cacheKey, data.id);
            return data.id;
          }
          const createResponse = await this.fetchWithAuth(
            `https://graph.microsoft.com/v1.0/me/drive/items/${parentIdForThisPart}/children`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                name: part,
                folder: {},
                "@microsoft.graph.conflictBehavior": "rename",
              }),
            }
          );
          if (!createResponse.ok) {
            throw new Error(`Create folder failed with status ${createResponse.status}`);
          }
          const createData = await createResponse.json();
          this.folderCache.set(cacheKey, createData.id);
          return createData.id;
        } catch (error) {
          console.error(`Error resolving path part ${part}:`, error);
          this.folderResolutionPromises.delete(cacheKey);
          throw error;
        }
      })();

      this.folderResolutionPromises.set(cacheKey, resolutionPromise);
      currentParentId = await resolutionPromise;
    }
    return { parentFolderId: currentParentId, fileName };
  }

  public async pull(itemPath: string, fileId_: string | null = null): Promise<any> {
    try {
      let downloadUrl: string;
      if (fileId_) {
        downloadUrl = `https://graph.microsoft.com/v1.0/me/drive/items/${fileId_}/content`;
      } else {
        const { parentFolderId, fileName: baseName } = await this.resolvePath(itemPath);
        if (!baseName || baseName === "null") return null;
        downloadUrl = `https://graph.microsoft.com/v1.0/me/drive/items/${parentFolderId}:/${baseName}:/content`;
      }

      const response = await this.fetchWithAuth(downloadUrl);
      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error(`Download failed with status ${response.status}`);
      }

      const fileContent = await response.text();
      if (!fileContent) return null;
      return JSON.parse(fileContent);
    } catch (error: any) {
      if (error.message && (error.message.includes("not found") || error.message.includes("404"))) {
        console.warn(`[Sync] Suppressing known error for ${itemPath}: ${error.message}`);
        return null;
      }
      console.error(`Error pulling ${itemPath}:`, error);
      Events.emit("showError", `Sync failed: ${error.message}`);
      throw error;
    }
  }

  private async uploadToOneDrive(
    fileName: string,
    data: any,
    isRetry: boolean = false
  ): Promise<PushResult | void> {
    try {
      const { parentFolderId, fileName: baseName } = await this.resolvePath(fileName);
      const fileContent = JSON.stringify(data);

      const response = await this.fetchWithAuth(
        `https://graph.microsoft.com/v1.0/me/drive/items/${parentFolderId}:/${baseName}:/content`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: fileContent,
        }
      );

      if (!response.ok) {
        if (response.status === 404 && !isRetry) {
          console.warn(
            `[OneDrive] Upload 404 for ${fileName}, clearing folder cache and retrying...`
          );
          this.folderCache.clear();
          this.folderResolutionPromises.clear();
          return this.uploadToOneDrive(fileName, data, true);
        }
        throw new Error(`Upload failed with status ${response.status}`);
      }

      const responseData = await response.json();
      if (responseData?.lastModifiedDateTime) {
        return { modifiedTime: responseData.lastModifiedDateTime };
      }
    } catch (error: any) {
      console.error(`Error uploading ${fileName}:`, error);
      throw error;
    }
  }

  public async push(fileName: string, data: any): Promise<PushResult | void> {
    return this.uploadToOneDrive(fileName, data);
  }

  public async uploadFile(
    filePath: string,
    fileContent: Blob | string | ArrayBuffer
  ): Promise<UploadResult> {
    try {
      const { parentFolderId, fileName: baseName } = await this.resolvePath(filePath);
      if (!baseName) throw new Error(`OneDrive uploadFile: could not resolve a file name for ${filePath}`);
      const fileName = baseName;
      let contentType = "application/octet-stream";
      let bodyContent: string | ArrayBuffer;

      if (fileContent instanceof Blob) {
        contentType = fileContent.type || "application/octet-stream";
        bodyContent = await fileContent.arrayBuffer();
      } else if (fileContent instanceof ArrayBuffer) {
        bodyContent = fileContent;
      } else if (typeof fileContent === "string") {
        if (fileName.endsWith(".json")) contentType = "application/json";
        else if (fileName.endsWith(".csv")) contentType = "text/csv";
        else if (fileName.endsWith(".html") || fileName.endsWith(".htm")) contentType = "text/html";
        else if (fileName.endsWith(".xml")) contentType = "application/xml";
        else contentType = "text/plain";
        bodyContent = fileContent;
      } else {
        bodyContent = String(fileContent);
      }

      const response = await this.fetchWithAuth(
        `https://graph.microsoft.com/v1.0/me/drive/items/${parentFolderId}:/${fileName}:/content`,
        { method: "PUT", headers: { "Content-Type": contentType }, body: bodyContent }
      );

      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
      }

      const responseData = await response.json();
      return { webUrl: responseData?.webUrl || undefined };
    } catch (error: any) {
      console.error(`Error uploading file ${filePath}:`, error);
      Events.emit("showError", `File upload failed: ${error.message}`);
      throw error;
    }
  }

  public async downloadFile(filePath: string): Promise<Blob> {
    const { parentFolderId, fileName: baseName } = await this.resolvePath(filePath);
    if (!baseName || baseName === "null") {
      throw new Error(`OneDrive downloadFile: file not found: ${filePath}`);
    }
    const response = await this.fetchWithAuth(
      `https://graph.microsoft.com/v1.0/me/drive/items/${parentFolderId}:/${baseName}:/content`
    );
    if (!response.ok) {
      throw new Error(`OneDrive download failed for ${filePath}: ${response.status}`);
    }
    return response.blob();
  }

  public async delete(filePath: string): Promise<void> {
    try {
      const { parentFolderId, fileName: baseName } = await this.resolvePath(filePath, false);
      if (!baseName || baseName === "null") {
        console.warn(`Cannot delete: invalid file name for path ${filePath}`);
        return;
      }

      const listResponse = await this.fetchWithAuth(
        `https://graph.microsoft.com/v1.0/me/drive/items/${parentFolderId}/children?$filter=name eq '${baseName}'`
      );
      if (!listResponse.ok) {
        throw new Error(`Search failed with status ${listResponse.status}`);
      }

      const listData = await listResponse.json();
      const fileId = listData.value?.[0]?.id;
      if (!fileId) {
        console.warn(`File not found for deletion: ${filePath}`);
        return;
      }

      const deleteResponse = await this.fetchWithAuth(
        `https://graph.microsoft.com/v1.0/me/drive/items/${fileId}`,
        { method: "DELETE" }
      );
      if (!deleteResponse.ok) {
        throw new Error(`Delete failed with status ${deleteResponse.status}`);
      }
      log(`Successfully deleted ${filePath} from OneDrive`);
    } catch (error) {
      console.error(`Error deleting ${filePath}:`, error);
      Events.emit("showError", `Delete failed: ${(error as any).message}`);
      throw error;
    }
  }

  public async pushDiff(globalEntries: any[]): Promise<void> {
    for (const entry of globalEntries) {
      await this.push(entry.path, entry.data);
      Events.emit("showStatus", { intent: "info", text: `Synced ${entry.path} to OneDrive.` });
    }
  }

  public async pullById(fileId: string): Promise<any> {
    try {
      const response = await this.fetchWithAuth(
        `https://graph.microsoft.com/v1.0/me/drive/items/${fileId}/content`
      );
      if (!response.ok) {
        throw new Error(`Download by ID failed with status ${response.status}`);
      }
      const fileContent = await response.text();
      return JSON.parse(fileContent);
    } catch (error) {
      console.error(`Error pulling by ID ${fileId}:`, error);
      Events.emit("showError", `Sync failed: ${(error as any).message}`);
      throw error;
    }
  }

  public async getMetadata(fileName: string): Promise<FileMetadata[]> {
    try {
      log(`[OneDrive] getMetadata called with fileName: ${fileName}`);
      const { parentFolderId } = await this.resolvePath(fileName);
      log(`[OneDrive] Resolved parentFolderId: ${parentFolderId}`);

      const verifyResponse = await this.fetchWithAuth(
        `https://graph.microsoft.com/v1.0/me/drive/items/${parentFolderId}`
      );
      if (!verifyResponse.ok) {
        throw new Error(
          `Folder not found (${verifyResponse.status}). Path may not have been created properly.`
        );
      }

      const listResponse = await this.fetchWithAuth(
        `https://graph.microsoft.com/v1.0/me/drive/items/${parentFolderId}/children?$select=id,name,folder,lastModifiedDateTime`
      );
      if (!listResponse.ok) {
        throw new Error(`List files failed with status ${listResponse.status}`);
      }
      const listData = await listResponse.json();
      return (listData.value || [])
        .filter((f: any) => !f.folder)
        .map((f: any) => ({
          id: f.id,
          name: f.name,
          path: f.name,
          modifiedTime: f.lastModifiedDateTime,
        }));
    } catch (error) {
      console.error(`Error getting metadata for ${fileName}:`, error);
      Events.emit("showError", `Sync failed: ${(error as any).message}`);
      throw error;
    }
  }

  public async getMetadataAll(projectName: string): Promise<FileMetadata[]> {
    const allFiles: FileMetadata[] = [];
    try {
      const normalizedProjectName = projectName.replace(/\/$/, "");

      const traverse = async (folderPath: string) => {
        const { parentFolderId } = await this.resolvePath(folderPath, true);
        const listResponse = await this.fetchWithAuth(
          `https://graph.microsoft.com/v1.0/me/drive/items/${parentFolderId}/children?$select=id,name,folder,lastModifiedDateTime`
        );
        if (!listResponse.ok) {
          throw new Error(`List files failed with status ${listResponse.status}`);
        }
        const listData = await listResponse.json();
        for (const file of listData.value || []) {
          if (file.folder) {
            await traverse(folderPath + "/" + file.name);
          } else {
            const pathParts = folderPath.split("/").filter((p: string) => p);
            let path: string;
            if (pathParts.length === 1) {
              path = normalizedProjectName + "/" + file.name;
            } else {
              const categoryPath = pathParts.slice(1).join("/");
              path = normalizedProjectName + "/" + categoryPath + "/" + file.name;
            }
            allFiles.push({
              id: file.id,
              name: file.name,
              path: path,
              modifiedTime: file.lastModifiedDateTime,
            });
          }
        }
      };

      await traverse(normalizedProjectName);
    } catch (error) {
      console.error(`Error getting metadata for all files under ${projectName}:`, error);
      Events.emit("showError", `Sync failed: ${(error as any).message}`);
      throw error;
    }
    return allFiles;
  }
}

const OneDrive = new OneDriveClass();
export default OneDrive;
